import React from "react";
import { CartesianGrid, Scatter, ScatterChart, Tooltip, XAxis, YAxis } from "recharts";
import { IndexCurveTypes } from "src/scenes/indexes-settings/constants";
import { round } from "src/utils/value-formatter";
import { Stack } from "@mui/material";
import styled from "styled-components";

const TooltipContainer = styled.div`
    background-color: ${({ theme }) => theme.palette.background.tooltip};
    padding: 0.5rem;
    min-width: 10rem;
    align-items: center;
    flex-direction: column;
    border-radius: 6px;
`;

const TooltipContentText = styled.p`
    font-size: 12px;
    margin: 0;
    color: ${({ color }) => color};
`;

const CustomTooltip = (props) => {
    const { active, payload } = props;

    if (active && payload && payload.length) {
        return (
            <TooltipContainer role="status" aria-live="assertive">
                {payload.map((entry) => {
                    return (
                        <Stack direction="row" spacing={0.5}>
                            <TooltipContentText color={entry.fill}>
                                {[entry.name]} :{" "}
                            </TooltipContentText>
                            <TooltipContentText>{round(entry.value, 2)}</TooltipContentText>
                        </Stack>
                    );
                })}
            </TooltipContainer>
        );
    }
    return null;
};

const FittingChart = ({ displayName, curve, normalization, weight, impact }) => {
    const data = React.useMemo(() => {
        const getValue = (i) => {
            return (normalization.max * i) / 100;
        };

        const getWeightedValue = (val) => {
            return Math.min(weight.coefficient * val, 1);
        };

        const getIndexValue = (val) => {
            let indexVal = 0;
            if (impact === "negative") {
                indexVal =
                    (normalization.max -
                        Math.max(Math.min(val, normalization.max), normalization.min)) /
                    (normalization.max - normalization.min);
            } else {
                indexVal =
                    Math.max(Math.min(val, normalization.max) - normalization.min, 0) /
                    (normalization.max - normalization.min);
            }

            if (curve.type === IndexCurveTypes.linear) {
                return indexVal * curve.coefficient + curve.constant;
            }
            if (curve.type === IndexCurveTypes.exponential) {
                return indexVal ** curve.power + curve.constant;
            }
            return indexVal;
        };

        const normal = [];
        const weighted = [];

        Array.from({ length: 101 }, (_, i) => {
            const val = getValue(i);
            const indexVal = getIndexValue(val);
            normal.push({
                index: indexVal,
                value: val,
            });
            weighted.push({
                index: getWeightedValue(indexVal),
                value: val,
            });
            return undefined;
        });
        return {
            normal,
            weighted,
        };
    }, [
        displayName,
        curve && JSON.stringify(curve),
        normalization && JSON.stringify(normalization),
        weight && JSON.stringify(weight),
        impact,
    ]);
    return (
        <Stack direction="column" alignItems="center">
            <h4 style={{ margin: "0" }}>Value Fitting Chart</h4>
            <ScatterChart
                width={300}
                height={200}
                margin={{
                    top: 10,
                    right: 10,
                    bottom: 10,
                    left: 10,
                    fontSize: "11px",
                }}
            >
                <CartesianGrid />
                <YAxis
                    yAxisId="left"
                    orientation="left"
                    type="number"
                    dataKey="index"
                    name="Index Value"
                    tick={{ fill: "#EAF0F4", opacity: "0.5", fontSize: "11px", fontWeight: "300" }}
                    label={{
                        value: "Index",
                        angle: -90,
                        position: "insideLeft",
                        fontSize: "11px",
                    }}
                />
                <YAxis
                    yAxisId="right"
                    orientation="right"
                    type="number"
                    dataKey="index"
                    name="Weighted Index Value"
                    tick={{ fill: "#EAF0F4", opacity: "0.5", fontSize: "11px", fontWeight: "300" }}
                    label={{
                        value: "Weighted Index (Final Contribution)",
                        angle: -90,
                        position: "insideTopRight",
                        fontSize: "11px",
                    }}
                />
                <XAxis
                    type="number"
                    dataKey="value"
                    name={`${displayName} value`}
                    tick={{ fill: "#EAF0F4", opacity: "0.5", fontSize: "11px", fontWeight: "300" }}
                    label={{
                        value: displayName,
                        position: "insideBottom",
                        offset: -10,
                        fontSize: "11px",
                    }}
                />
                <Tooltip
                    cursor={{ strokeDasharray: "3 3", opacity: "0.2" }}
                    content={CustomTooltip}
                />
                <Scatter yAxisId="left" name="Index" data={data.normal} fill="#8884d8" line />
                <Scatter
                    yAxisId="right"
                    name="Weighted Index"
                    data={data.weighted}
                    fill="#82ca9d"
                    line
                    shape="diamond"
                />
            </ScatterChart>
        </Stack>
    );
};
export default FittingChart;
